import { useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Input, List, Modal, notification, Row, Select } from 'antd';

import { Attendance } from 'components/Pages/Admin/CheckinPage/QrCodeScanner/types';
import eventStaffService from 'services/eventStaff';

import { EmptyList } from './EmptyList';
import styles from './styles.module.css';
import AttendanceCard from '../AttendanceCard';

enum SearchType {
  TICKET_ID = 'invite_key',
  DOCUMENT = 'document',
  INVOICE_ID = 'invoice_id'
}

export const ManualCheckin = ({
  staffToken,
  open,
  onClose
}: {
  staffToken: string;
  open: boolean;
  onClose: () => void;
}) => {
  const [searchType, setSearchType] = useState<SearchType>(SearchType.TICKET_ID);
  const [searchValue, setSearchValue] = useState('');
  const [showList, setShowList] = useState(false);
  const [list, setList] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(false);
  const [disabledAction, setDisabledAction] = useState(false);

  useEffect(() => {
    localStorage.getItem('searchType');
    setSearchType(SearchType.TICKET_ID);
  }, []);

  const handleSearch = () => {
    if (!searchValue) {
      return;
    }

    if (searchType === SearchType.INVOICE_ID && isNaN(Number(searchValue))) {
      return notification.error({ message: 'Busca por fatura deve conter apenas números.' });
    }

    setLoading(true);
    setShowList(true);

    eventStaffService.searchTicket(searchType, searchValue, staffToken).subscribe({
      next: (response: any) => {
        if (response.length === 0) {
          setList([]);
        } else {
          setList(response);
        }

        setLoading(false);
      },
      error: (err: any) => {
        if (err.response?.status === 404) {
          setList([]);
          setLoading(false);
          return;
        }

        notification.error({ message: 'Erro ao buscar participante' });
        setLoading(false);
        setShowList(false);
      }
    });
  };

  const updateAttendance = (updatedAttendance: Attendance) => {
    setList(prevList =>
      prevList.map(item => (item.invite_key === updatedAttendance.invite_key ? updatedAttendance : item))
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      mask={false}
      centered
      afterClose={() => {
        setList([]);
        setShowList(false);
      }}
      destroyOnHidden
      className={styles.modal}
      wrapClassName={styles.modalWrapper}
      styles={{
        content: {
          padding: '20px 20px 120px 20px',
          margin: '0',
          width: '100vw',
          height: '100vh',
          maxWidth: '450px',
          overflowY: 'scroll'
        }
      }}
    >
      <div className={styles.wrapper}>
        <p className={styles.title}>Check-in Manual</p>
        <p className={styles.description}>Busque o participante e realize o check-in manual.</p>
        <Row gutter={[4, 4]} style={{ marginTop: '16px' }}>
          <Col span={24}>
            <Select
              value={searchType}
              options={[
                { label: 'Cód. Ingresso', value: SearchType.TICKET_ID },
                { label: 'CPF/CNPJ', value: SearchType.DOCUMENT },
                { label: 'Fatura', value: SearchType.INVOICE_ID }
              ]}
              onChange={e => {
                setSearchType(e);
                localStorage.setItem('searchType', searchType);
              }}
              className={styles.fullWidth}
              size='large'
            />
          </Col>
          <Col span={24}>
            <div className={styles.searchWrapper}>
              <Input onChange={e => setSearchValue(e.target.value)} className={styles.searchInput} size='large' />
              <Button onClick={handleSearch} className={styles.searchButton} size='large'>
                <SearchOutlined />
              </Button>
            </div>
          </Col>
          <Col span={24}>
            <p className={styles.searchDescription}>
              Busque pelo cód. do ingresso, nome, CPF/CNPJ ou número da fatura.
            </p>
          </Col>
        </Row>
      </div>
      {showList && (
        <>
          <Divider />
          <div className={styles.wrapper}>
            <p className={styles.searchResults}>Resultados da busca</p>
            <List
              itemLayout='vertical'
              renderItem={item => {
                return (
                  <AttendanceCard
                    attendance={item}
                    staffToken={staffToken}
                    onUpdateAttendance={updateAttendance}
                    disabledAction={disabledAction}
                    setDisabledAction={pending => setDisabledAction(pending)}
                  />
                );
              }}
              dataSource={list}
              loading={loading}
              locale={{ emptyText: <EmptyList /> }}
              style={{ marginTop: '16px' }}
            />
          </div>
        </>
      )}
    </Modal>
  );
};
